import { Component } from '@angular/core';
import { Type } from 'class-transformer';
import { FormulaEditorDialog } from '../../../../components/common/formulaEditor/formulaEditor.control';
import { ClassTransformerHelper } from '../../../../helpers/class.transformer.helper';
import { VariablesNodeInformation } from '../../../../models/basic/formulaEditor.model';
import { FormulaInputMode } from '../../../../models/enums/formulainputmode.enum';
import { WorkflowType } from '../../../../models/enums/workflowtype.enum';
import { WorkflowExitInfo, WorkflowModuleSettingsHelper, WorkflowRegistry } from '../../../../services/workflow.service';
import { DataCheck, FormulaWorkflowDialogContent } from '../../../../workflow/workflow.dialog';

/**
 * "Zuordnen" (mapWFModule, 2026-10-20): records of a list -- typically the JSON answer of the REST
 * module -- into rows or objects by one mapping table; optionally one row per element of a sub-list
 * (1:n). Server: MiddleShared/Workflow/DataOperations/Zuordnen/ZuordnenSettingsData.cs.
 */
@Component({
    selector: 'wf-zuordnen-settings',
    templateUrl: './zuordnen.settings.html',
    styles: ['.zeile { align-items: center; gap: 4px; flex-wrap: wrap; } .pfad { min-width: 220px; } .ziel { min-width: 160px; } .hinweis { opacity: .7; font-size: 12px; }']
})
export class ZuordnenSettings extends FormulaWorkflowDialogContent {
    Data = new ZuordnenSettingsData();
    readonly Herkuenfte = [
        { Value: ZuordnungsHerkunft.Record, Caption: '@@Aus dem Datensatz' },
        { Value: ZuordnungsHerkunft.Element, Caption: '@@Aus der Position' },
        { Value: ZuordnungsHerkunft.Formula, Caption: '@@Formel' },
    ];
    readonly Typen = [
        { Value: ZuordnungsTyp.Auto, Caption: '@@Automatisch' },
        { Value: ZuordnungsTyp.Text, Caption: '@@Text' },
        { Value: ZuordnungsTyp.Integer, Caption: '@@Ganzzahl' },
        { Value: ZuordnungsTyp.Decimal, Caption: '@@Dezimalzahl' },
        { Value: ZuordnungsTyp.Boolean, Caption: '@@Bool' },
        { Value: ZuordnungsTyp.Date, Caption: '@@Datum' },
        { Value: ZuordnungsTyp.Guid, Caption: '@@Guid' },
    ];

    public static GetRegistry(): WorkflowRegistry {
        const reg = new WorkflowRegistry();
        reg.ID = 'mapWFModule';
        reg.Caption = '@@Zuordnen';
        reg.GroupID = 'reldataoperations';
        reg.Index = 75;
        reg.SettingsControl = ZuordnenSettings;
        reg.SettingsTypeHelper = new ZuordnenSettingsDataHelper();
        reg.WorkflowType = WorkflowType.Service;
        return reg;
    }

    initialize(data: any) {
        super.initialize(data);
        if (data) {
            this.Data = ClassTransformerHelper.CopyObject(data, ZuordnenSettingsData);
        }
        if (!this.Data.Mappings) {
            this.Data.Mappings = [];
        }
    }

    getResult(): any {
        this.Data.Mappings = this.Data.Mappings.filter(x => x.Target);
        return this.Data;
    }

    checkData(): DataCheck {
        const retVal = new DataCheck();
        if (!this.Data.Mappings.some(x => x.Target)) {
            retVal.IsCorrect = false;
            retVal.Error = '@@Bitte mindestens eine Zuordnung mit Ziel anlegen';
        } else if (this.Data.Mappings.some(x => x.Target && x.From === ZuordnungsHerkunft.Formula && !x.Value)) {
            retVal.IsCorrect = false;
            retVal.Error = '@@Bitte fuer jede Formel-Zuordnung eine Formel angeben';
        }
        return retVal;
    }

    addMapping() {
        this.Data.Mappings.push(new ZuordnungsZeile());
    }

    removeMapping(i: number) {
        this.Data.Mappings.splice(i, 1);
    }

    moveMapping(i: number, richtung: number) {
        const j = i + richtung;
        if (j >= 0 && j < this.Data.Mappings.length) {
            const [z] = this.Data.Mappings.splice(i, 1);
            this.Data.Mappings.splice(j, 0, z);
        }
    }

    /** Formula editor for the value (From = Formula) or the condition; record and element are variables. */
    /** Whether the records are unfolded: then values can come from the element. */
    get aufgeteilt(): boolean {
        return this.Data.UnfoldMode === ZuordnenAufteilen.DateRange || !!this.Data.UnfoldPath;
    }

    showFormulaDialog(zeile: any, feld: string) {
        const variables = [...this.Variables];
        [this.Data.RecordName || 'satz', this.Data.ElementName || 'element'].forEach(name => {
            if (!variables.some(x => x.Name === name)) {
                const vni = new VariablesNodeInformation();
                vni.Name = name;
                vni.AliasKey = name;
                vni.VariableID = name;
                variables.push(vni);
            }
        });
        FormulaEditorDialog.ShowDialog({ Variables: variables, InputMode: FormulaInputMode.VariableName, Formula: zeile[feld] }, result => {
            if (result && result.Formula !== undefined) {
                zeile[feld] = result.Formula;
            }
            return true;
        });
    }
}

export enum ZuordnungsHerkunft { Record, Element, Formula }
export enum ZuordnungsTyp { Auto, Text, Integer, Decimal, Boolean, Date, Guid }
export enum ZuordnenAusgabe { Rows, Objects }
export enum ZuordnenAufteilen { SubList, DateRange }
export enum ZuordnenSchritt { Day, Week, Month }

export class ZuordnungsZeile {
    Target = '';
    From: ZuordnungsHerkunft = ZuordnungsHerkunft.Record;
    Value = '';
    Type: ZuordnungsTyp = ZuordnungsTyp.Auto;
    Condition = '';
    OnlyIfNotEmpty = false;
}

// @dynamic
export class ZuordnenSettingsData {
    SourceKey = '';
    ListPath = '';
    UnfoldPath = '';
    KeepWithoutElements = false;
    UnfoldMode: ZuordnenAufteilen = ZuordnenAufteilen.SubList;
    UnfoldFrom = '';
    UnfoldTo = '';
    UnfoldStep: ZuordnenSchritt = ZuordnenSchritt.Day;
    UnfoldWorkdaysOnly = false;
    RecordName = 'satz';
    ElementName = 'element';
    @Type(() => ZuordnungsZeile)
    Mappings: ZuordnungsZeile[] = [];
    Output: ZuordnenAusgabe = ZuordnenAusgabe.Rows;
    ResultKey = '';
}

const DATENTYP: { [t: number]: string } = {
    [ZuordnungsTyp.Text]: 'System.String', [ZuordnungsTyp.Integer]: 'System.Int64', [ZuordnungsTyp.Decimal]: 'System.Decimal',
    [ZuordnungsTyp.Boolean]: 'System.Boolean', [ZuordnungsTyp.Date]: 'System.DateTime', [ZuordnungsTyp.Guid]: 'System.Guid',
};

export class ZuordnenSettingsDataHelper extends WorkflowModuleSettingsHelper {
    constructor() {
        super();
        // The exit carries rows or objects depending on Output.
        this.MustUpdateExitPoints = true;
    }

    getExitPoints(settings: any): WorkflowExitInfo[] {
        const retVal = new WorkflowExitInfo();
        if (!settings || settings.Output !== ZuordnenAusgabe.Objects) {
            retVal.Type = 'relData';
        }
        return [retVal];
    }

    getEmptySettingsInstance() {
        return new ZuordnenSettingsData();
    }

    /** Rows: the following modules see the target columns (editcolumns, exportData). */
    async fillActualState(module, state, wfData) {
        const settings = WorkflowModuleSettingsHelper.GetSettingsFromModule(module);
        if (state && settings && settings.Output !== ZuordnenAusgabe.Objects && !settings.ResultKey) {
            state.set('DataSource', {
                Columns: (settings.Mappings ?? []).filter(x => x.Target).map(x => ({ Name: x.Target, DataTyp: DATENTYP[x.Type] ?? 'System.String' }))
            });
        }
        if (state && settings?.ResultKey) {
            WorkflowModuleSettingsHelper.AddStatusKeysToState(state, [settings.ResultKey]);
        }
    }
}
